<template>
  <cui-filter-wrapper :render-clear-button="false" class="mb-0 filter-wrapper">
    <div class="devices-filters__content d-flex flex-column ga-3">
      <v-select
        v-model="deviceStatusesModel"
        :items="deviceStatusItems"
        :disabled="disabled"
        bg-color="white"
        menu-icon="icon-arrow-down-xs"
        item-value="id"
        item-title="name"
        :menu-props="{ maxHeight: '300' }"
        class="status-filter filter-menu mt-2"
        :class="{ 'filter-active': deviceStatusesModel?.length }"
        :placeholder="t('devices.filters.status')"
        data-testid="export-devices-status-filter"
        density="compact"
        variant="outlined"
        multiple
        rounded
        hide-details
      >
        <template #selection="{ index }">
          <span v-if="index === 0" class="body2">{{ t("devices.filters.status") }}</span>
        </template>
        <template #label>
          <span v-if="deviceStatusesModel?.length" class="filter-label">
            {{ deviceStatusesModel.length }}
          </span>
        </template>
        <template #item="{ item, props: itemProps }">
          <v-list-item v-bind="itemProps" title="">
            <template #prepend="{ isActive }">
              <v-list-item-action start>
                <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
              </v-list-item-action>
            </template>

            <v-list-item-title
              :data-testid="`export-devices-status-filter-${item.raw.name}-item`"
              class="d-flex align-center label-popup-block"
              title=""
            >
              <span class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
              <span>{{ getFilterItemDisplayName(item.raw as DeviceStatusFilterItem) }}</span>
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-select>

      <v-select
        v-if="!hideAgentHealth"
        v-model="agentHealthModel"
        :items="agentHealthItems"
        :disabled="disabled"
        bg-color="white"
        menu-icon="icon-arrow-down-xs"
        item-value="id"
        item-title="name"
        :menu-props="{ maxHeight: '300' }"
        class="agent-health-filter filter-menu"
        :class="{ 'filter-active': agentHealthModel?.length }"
        :placeholder="t('devices.filters.agentHealth')"
        data-testid="export-devices-agent-health-filter"
        density="compact"
        variant="outlined"
        multiple
        rounded
        hide-details
      >
        <template #selection="{ index }">
          <span v-if="index === 0" class="body2">{{ t("devices.filters.agentHealth") }}</span>
        </template>
        <template #label>
          <span v-if="agentHealthModel?.length" class="filter-label">
            {{ agentHealthModel.length }}
          </span>
        </template>
        <template #item="{ item, props: itemProps }">
          <v-list-item v-bind="itemProps" title="">
            <template #prepend="{ isActive }">
              <v-list-item-action start>
                <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
              </v-list-item-action>
            </template>

            <v-list-item-title
              :data-testid="`export-devices-agent-health-filter-${item.raw.name}-item`"
              class="d-flex align-center label-popup-block"
              title=""
            >
              <span class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
              <span>{{ getFilterItemDisplayName(item.raw as DeviceStatusFilterItem) }}</span>
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-select>

      <labels-filter
        v-model="labelsModel"
        :items="labelOnlyItems"
        :disabled="disabled"
        :placeholder="t('devices.filters.labels')"
        :no-data-text="t('devices.filters.label.noData')"
        class="label-filter filter-menu"
        :class="{ 'filter-active': labelsModel?.length }"
        data-test-id="export-devices-label-filter"
      />

      <v-select
        :model-value="osVersionModel"
        :items="osVersionItems()"
        :disabled="disabled"
        :menu-props="{ maxHeight: '300' }"
        class="os-version-filter filter-menu fit"
        :class="{ 'filter-active': osVersionModel }"
        :placeholder="t('devices.filters.osVersion')"
        data-testid="export-devices-os-version-filter"
        density="compact"
        item-value="name"
        item-title="name"
        rounded
        hide-details
        bg-color="white"
        @update:model-value="onOsVersionChange"
      >
        <template #selection="{ item }">
          <span class="body2">
            {{ item.title }}
          </span>
        </template>
        <template #item="{ item, props: itemProps }">
          <v-list-subheader v-if="item.raw.subheader" :title="item.raw.name" />
          <v-list-item
            v-else
            :data-testid="`export-devices-os-version-filter-${item.raw.name}-item`"
            v-bind="itemProps"
          >
          </v-list-item>
        </template>
      </v-select>

      <device-coro-version-filter
        v-model="clientVersionModel"
        :items="clientVersionItems(osVersionModel)"
        :disabled="disabled"
        class="agent-version-filter filter-menu fit"
        :class="{ 'filter-active': clientVersionModel }"
        data-test-id="export-devices-client-version-filter"
      />

      <device-vulnerability-filter
        v-model="vulnerabilitiesModel"
        :disabled="disabled"
        class="vulnerabilities-filter filter-menu"
        :class="{ 'filter-active': vulnerabilitiesModel && vulnerabilitiesModel.length }"
        data-test-id="export-devices-vulnerabilities-filter"
      />
    </div>
  </cui-filter-wrapper>
</template>

<script setup lang="ts" generic="T extends DevicesFilters">
import { toRef, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import type { DevicesFilters } from "@/_store/filters.module";
import type { DeviceOsOrClientVersions, DeviceStatusFilterItem } from "@/_store/devices.module";
import type { DeviceLabel } from "@/_store/devices-settings.module";
import { CuiFilterWrapper } from "@coronet/cui";
import { useDevicesFilters } from "@/composables/devices/useDevicesFilters";
import { useTwoWayBinding } from "@/composables/useTwoWayBinding";
import DeviceVulnerabilityFilter from "@/components/filters/DeviceVulnerabilityFilter.vue";
import LabelsFilter from "@/components/filters/LabelsFilter.vue";
import DeviceCoroVersionFilter from "@/components/filters/DeviceCoroVersionFilter.vue";

const props = withDefaults(
  defineProps<{
    modelValue: T;
    labels?: DeviceLabel[];
    statuses?: DeviceStatusFilterItem[];
    rawOsVersions?: DeviceOsOrClientVersions | null;
    rawClientVersions?: DeviceOsOrClientVersions | null;
    disabled?: boolean;
    hideAgentHealth?: boolean;
  }>(),
  {
    labels: () => [],
    statuses: () => [],
    rawOsVersions: () => ({}),
    rawClientVersions: () => ({}),
    disabled: false,
    hideAgentHealth: false,
  }
);

const emit = defineEmits<{
  (event: "update:modelValue", value: T): void;
}>();

const { t } = useI18n();

const localFilters = useTwoWayBinding(toRef(props, "modelValue"), (value) => {
  emit("update:modelValue", value);
});

// Changing the OS version invalidates the selected client version, so emit both
// changes as a single update — emitting them separately would re-read the stale
// prop before the parent syncs the new OS version back.
const onOsVersionChange = (value: string | null) => {
  localFilters.value = { ...localFilters.value, osVersion: value, clientVersion: null };
};

const { labels, statuses, rawOsVersions, rawClientVersions } = toRefs(props);

const {
  deviceStatusesModel,
  agentHealthModel,
  labelsModel,
  osVersionModel,
  clientVersionModel,
  vulnerabilitiesModel,
  deviceStatusItems,
  agentHealthItems,
  labelOnlyItems,
  osVersionItems,
  clientVersionItems,
  getFilterItemDisplayName,
} = useDevicesFilters<T>({
  modelValue: localFilters,
  emitUpdate: (value) => {
    localFilters.value = value;
  },
  labels,
  statuses,
  rawOsVersions,
  rawClientVersions,
});

defineExpose({
  osVersionItems,
  clientVersionItems,
  deviceStatusItems,
  agentHealthItems,
  labelOnlyItems,
});
</script>

<style scoped lang="scss">
.devices-filters__content {
  width: 100%;
}

:deep(*) {
  .label-popup-block span {
    flex: unset !important;
  }
}

.color-preview {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  border-radius: 2px !important;
}

.devices-filters__content {
  .status-filter,
  .agent-health-filter,
  .label-filter,
  .os-version-filter,
  .agent-version-filter,
  .vulnerabilities-filter {
    width: 240px;
    max-width: 240px;
  }
}
</style>
